import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';

interface Props {
  /** Called with the two decisions (A = OCR, B = verification); resolves when they are recorded. */
  onDecide: (ocr: boolean, verification: boolean) => Promise<void> | void;
  /** Pre-select from a previous decision when reopened from My Documents. */
  initialOcr?: boolean;
  initialVerification?: boolean;
}

/**
 * Consent for Document Processing and Identity Verification — client wording, 2026-10-21
 * (plan P5 / P17 decision 4). Two consents, recorded separately: A gates AuthBridge OCR,
 * B gates the government-source verification. Shown once before the first upload; either
 * answer is recorded so it never re-appears, and both can be changed from My Documents.
 */
export const VerificationConsentModal: React.FC<Props> = ({ onDecide, initialOcr = false, initialVerification = false }) => {
  const [ocr, setOcr] = useState(initialOcr);
  const [verification, setVerification] = useState(initialVerification);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onDecide(ocr, verification);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show backdrop="static" keyboard={false} centered size="lg" scrollable>
      <Modal.Header>
        <Modal.Title className="fs-6">Consent for Document Processing and Identity Verification</Modal.Title>
      </Modal.Header>
      <Modal.Body className="small">
        <p>
          To simplify your onboarding process and assist in verifying the information and documents you provide,
          Facilon Services Private Limited (&ldquo;Facilon&rdquo;), acting on behalf of your selected Service Provider
          where applicable, uses authorised technology and verification service providers, including AuthBridge
          Research Services Private Limited (&ldquo;AuthBridge&rdquo;).
        </p>

        <div className="border rounded p-2 mb-2">
          <div className="form-check">
            <input
              id="consent-ocr"
              className="form-check-input"
              type="checkbox"
              checked={ocr}
              onChange={(e) => setOcr(e.target.checked)}
              disabled={busy}
            />
            <label className="form-check-label fw-semibold" htmlFor="consent-ocr">
              A. Consent for Document Processing and OCR
            </label>
          </div>
          <p className="mb-1 mt-2">
            I consent to Facilon and its authorised technology service providers processing my uploaded identity,
            address and other onboarding documents using Optical Character Recognition (OCR) and related automated
            technologies to extract information, prepopulate application forms, and identify missing or inconsistent
            information.
          </p>
          <p className="mb-0">
            I understand that I will have the opportunity to review and correct extracted information before
            submitting my application.
          </p>
        </div>

        <div className="border rounded p-2 mb-2">
          <div className="form-check">
            <input
              id="consent-verification"
              className="form-check-input"
              type="checkbox"
              checked={verification}
              onChange={(e) => setVerification(e.target.checked)}
              disabled={busy}
            />
            <label className="form-check-label fw-semibold" htmlFor="consent-verification">
              B. Consent for Identity and Document Verification
            </label>
          </div>
          <p className="mb-1 mt-2">
            I authorise Facilon, acting on behalf of my selected Service Provider where applicable, to securely share
            the necessary personal information and documents with AuthBridge and its authorised verification providers
            for the following purposes:
          </p>
          <ul className="mb-1">
            <li>Verification of my PAN and related identification particulars.</li>
            <li>Verification of identification and/or address documents where the relevant verification services are available.</li>
            <li>Validation of document authenticity, consistency and relevant information against legally accessible and authorised verification sources.</li>
            <li>Sharing the resulting verification status, findings and any identified discrepancies with Facilon and the selected Service Provider for my onboarding application.</li>
          </ul>
          <p className="mb-1">
            I understand that verification results are used to support the Service Provider&rsquo;s onboarding and
            compliance processes and that the Service Provider retains responsibility for its own customer acceptance
            and applicable KYC decisions.
          </p>
          <p className="mb-0">
            I understand that I may withdraw my consent through the applicable process, subject to legal requirements
            and any processing already lawfully undertaken.
          </p>
        </div>

        <p className="fw-semibold mb-1">Confirmation</p>
        <p className="mb-0">
          By selecting the relevant checkbox(es) and clicking &ldquo;Continue&rdquo;, I confirm that I have read and
          understood the purposes described above and voluntarily consent to the selected processing activities.
        </p>
        {!ocr && !verification && (
          <p className="text-muted mb-0 mt-2">
            Continuing without a selection means your documents are read by Facilon&rsquo;s standard reader only and
            no verification is performed. You can change this later from My Documents.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Saving...' : 'Continue'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default VerificationConsentModal;
